import type React from "react";

export interface IconProps {
  wrapperClassName: string;
  svgClassName: string;
}

/** Loading spinner
 *
 * @param {IconProps} props - Icon properties
 * @param {string} props.wrapperClassName - Wrapper class name
 * @param {string} props.svgClassName - SVG class name
 *
 * Credit: https://tailwindcss.com/docs/animation#spin (license: MIT)
 */
export const LoadingIcon: React.FC<React.PropsWithChildren<IconProps>> = ({
  wrapperClassName = "",
  svgClassName = "",
}) => (
  <span className={wrapperClassName}>
    <svg
      aria-label="Loading"
      className={`animate-spin ${svgClassName}`}
      fill="none"
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        fill="currentColor"
      />
    </svg>
  </span>
);

/**
 * GitPullLogo icon
 *
 * @param {IconProps} props - Icon properties
 * @param {string} props.wrapperClassName - Optional wrapper class
 * @param {string} props.svgClassName - Optional SVG class
 */
export const GitPullLogoIcon: React.FC<React.PropsWithChildren<IconProps>> = ({
  wrapperClassName = "",
  svgClassName = "h-12 w-12",
}) => (
  <span className={wrapperClassName}>
    <svg
      aria-label="git-pull"
      className={svgClassName}
      fill="none"
      role="img"
      shapeRendering="crispEdges"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Dark mode shapes */}
      <g
        className="hidden dark:block"
        fill="none"
        stroke="white"
        strokeLinecap="square"
        strokeWidth="3"
      >
        <line
          className="git-pull-logo--dark--top-line"
          x1="6"
          x2="26"
          y1="8"
          y2="8"
        />
        <line
          className="git-pull-logo--dark--middle-line"
          x1="6"
          x2="26"
          y1="16"
          y2="16"
        />
        <line
          className="git-pull-logo--dark--bottom-line"
          x1="6"
          x2="26"
          y1="24"
          y2="24"
        />
      </g>

      {/* Light mode shapes */}
      <g
        className="block dark:hidden"
        fill="none"
        stroke="black"
        strokeLinecap="square"
        strokeWidth="3"
      >
        <line
          className="git-pull-logo--light--top-line"
          x1="6"
          x2="26"
          y1="8"
          y2="8"
        />
        <line
          className="git-pull-logo--light--middle-line"
          x1="6"
          x2="26"
          y1="16"
          y2="16"
        />
        <line
          className="git-pull-logo--light--bottom-line"
          x1="6"
          x2="26"
          y1="24"
          y2="24"
        />
      </g>
    </svg>
  </span>
);

GitPullLogoIcon.displayName = "GitPullLogoIcon";

export const GitPullRequestIcon: React.FC<
  React.PropsWithChildren<IconProps>
> = ({ wrapperClassName = "", svgClassName = "" }) => (
  <span
    className={`${wrapperClassName} -start-3 flex items-center justify-center rounded-full bg-green-100 ring ring-white dark:bg-green-900 dark:ring-emerald-900`}
  >
    {/* Credit: git-pull-request, https://primer.style/foundations/icons/git-pull-request-16 */}
    <svg
      aria-hidden="true"
      className={`${svgClassName} text-green-800 dark:text-green-300`}
      fill="currentColor"
      height="16"
      viewBox="0 0 16 16"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1.5 3.25a2.25 2.25 0 1 1 3 2.122v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.25 2.25 0 0 1 1.5 3.25Zm5.677-.177L9.573.677A.25.25 0 0 1 10 .854V2.5h1A2.5 2.5 0 0 1 13.5 5v5.628a2.251 2.251 0 1 1-1.5 0V5a1 1 0 0 0-1-1h-1v1.646a.25.25 0 0 1-.427.177L7.177 3.427a.25.25 0 0 1 0-.354ZM3.75 2.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm0 9.5a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm8.25.75a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Z" />
    </svg>
  </span>
);

export const GitPullRequestClosedIcon: React.FC<
  React.PropsWithChildren<IconProps>
> = ({ wrapperClassName = "", svgClassName = "" }) => (
  <span
    className={`${wrapperClassName} -start-3 flex items-center justify-center rounded-full bg-red-100 ring ring-white dark:bg-red-900 dark:ring-emerald-900`}
  >
    <svg
      aria-hidden="true"
      className={`${svgClassName} text-red-800 dark:text-red-300`}
      fill="currentColor"
      height="16"
      viewBox="0 0 16 16"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Credit: git-pull-request-closed, https://primer.style/foundations/icons/git-pull-request-closed-16 */}
      <path d="M3.25 1A2.25 2.25 0 0 1 4 5.372v5.256a2.251 2.251 0 1 1-1.5 0V5.372A2.251 2.251 0 0 1 3.25 1Zm9.5 5.5a.75.75 0 0 1 .75.75v3.378a2.251 2.251 0 1 1-1.5 0V7.25a.75.75 0 0 1 .75-.75Zm-2.03-5.273a.75.75 0 0 1 1.06 0l.97.97.97-.97a.748.748 0 0 1 1.265.332.75.75 0 0 1-.205.729l-.97.97.97.97a.751.751 0 0 1-.018 1.042.751.751 0 0 1-1.042.018l-.97-.97-.97.97a.749.749 0 0 1-1.275-.326.749.749 0 0 1 .215-.734l.97-.97-.97-.97a.75.75 0 0 1 0-1.06ZM2.5 3.25a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0ZM3.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Zm9.5 0a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" />
    </svg>
  </span>
);

export const GitMergeIcon: React.FC<React.PropsWithChildren<IconProps>> = ({
  wrapperClassName = "",
  svgClassName = "",
}) => (
  <span
    className={`${wrapperClassName} -start-3 flex items-center justify-center rounded-full bg-purple-100 ring ring-white dark:bg-purple-900 dark:ring-emerald-900`}
  >
    {/* Credit: git-merge, https://primer.style/foundations/icons/git-merge-16 */}
    <svg
      aria-hidden="true"
      className={`${svgClassName} text-purple-800 dark:text-purple-300`}
      fill="currentColor"
      height="16"
      viewBox="0 0 16 16"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5.45 5.154A4.25 4.25 0 0 0 9.25 7.5h1.378a2.251 2.251 0 1 1 0 1.5H9.25A5.734 5.734 0 0 1 5 7.123v3.505a2.25 2.25 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.95-.218ZM4.25 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm8.5-4.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM5 3.25a.75.75 0 1 0 0 .005V3.25Z" />
    </svg>
  </span>
);

export const GitMergeQueueIcon: React.FC<
  React.PropsWithChildren<IconProps>
> = ({ wrapperClassName = "", svgClassName = "" }) => (
  <span
    className={`${wrapperClassName} -start-3 flex items-center justify-center rounded-full bg-gray-300 dark:bg-gray-700`}
  >
    <svg
      aria-hidden="true"
      className={`${svgClassName} text-gray-700 dark:text-gray-100`}
      fill="currentColor"
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Credit: git-merge-queue-16, https://primer.style/foundations/icons/git-merge-queue-16 */}
      <path d="M3.75 4.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5ZM3 7.75a.75.75 0 0 1 1.5 0v2.878a2.251 2.251 0 1 1-1.5 0Zm.75 5.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm5-7.75a1.25 1.25 0 1 1-2.5 0 1.25 1.25 0 0 1 2.5 0Zm5.75 2.5a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-1.5 0a.75.75 0 1 0-1.5 0 .75.75 0 0 0 1.5 0Z" />
    </svg>
  </span>
);

export const DiffIcon: React.FC<React.PropsWithChildren<IconProps>> = ({
  wrapperClassName = "",
  svgClassName = "",
}) => (
  <span
    className={`${wrapperClassName} -start-3 flex items-center justify-center rounded-full ring ring-white dark:ring-emerald-900`}
  >
    <svg
      aria-hidden="true"
      className={`${svgClassName}`}
      fill="currentColor"
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Credit: git-diff-16, https://primer.style/foundations/icons/git-diff-16 */}
      <path d="M8.75 1.75V5H12a.75.75 0 0 1 0 1.5H8.75v3.25a.75.75 0 0 1-1.5 0V6.5H4A.75.75 0 0 1 4 5h3.25V1.75a.75.75 0 0 1 1.5 0ZM4 13h8a.75.75 0 0 1 0 1.5H4A.75.75 0 0 1 4 13Z" />
    </svg>
  </span>
);

/** ------------------------------------------------------------------
 *  ChevronDownIconProps
 *  ------------------------------------------------------------------ */
/**
 * Extends the standard `React.SVGProps<SVGSVGElement>` to allow
 * additional customization, such as CSS classes.
 */
export interface ChevronDownIconProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Any additional Tailwind or custom classes.
   */
  className?: string;
}

/** ------------------------------------------------------------------
 *  ChevronDownIcon
 *  ------------------------------------------------------------------ */
/**
 * Renders a chevron-down icon as an inline SVG.
 *
 * @param props - `ChevronDownIconProps` for configuring the SVG element.
 * @returns A SVG representing a chevron-down arrow.
 *
 * @example
 * ```tsx
 * <ChevronDownIcon className="h-5 w-5 text-gray-500" />
 * ```
 */
export const ChevronDownIcon: React.FC<ChevronDownIconProps> = ({
  className,
  ...svgProps
}) => {
  return (
    <svg
      aria-hidden="true"
      className={className ?? "h-5 w-5"}
      fill="currentColor"
      viewBox="0 0 20 20"
      {...svgProps}
    >
      <path
        clipRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 10.9l3.71-3.67a.75.75 0 011.04 1.08l-4.25 4.2a.75.75 0 01-1.06 0l-4.25-4.2a.75.75 0 01.02-1.06z"
        fillRule="evenodd"
      />
    </svg>
  );
};

/** ------------------------------------------------------------------
 *  ArrowLeftIconProps
 *  ------------------------------------------------------------------ */
/**
 * Extends the standard `React.SVGProps<SVGSVGElement>` to allow
 * additional customization, such as CSS classes.
 */
export interface ArrowLeftIconProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Any additional Tailwind or custom classes.
   */
  className?: string;
}

/** ------------------------------------------------------------------
 *  ArrowLeftIcon
 *  ------------------------------------------------------------------ */
/**
 * Renders an arrow-left icon as an inline SVG.
 *
 * @param props - `ArrowLeftIconProps` for configuring the SVG element.
 * @returns A SVG representing a left arrow.
 *
 * @example
 * ```tsx
 * <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
 * ```
 */
export const ArrowLeftIcon: React.FC<ArrowLeftIconProps> = ({
  className,
  ...svgProps
}) => {
  return (
    <svg
      aria-hidden="true"
      className={className ?? "h-5 w-5"}
      fill="currentColor"
      viewBox="0 0 20 20"
      {...svgProps}
    >
      <path
        clipRule="evenodd"
        d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z"
        fillRule="evenodd"
      />
    </svg>
  );
};

export interface TableOfContentsIconProps extends IconProps {}

/**
 * Table of Contents icon for mobile menu toggle
 * Icon credit: Furo theme by Pradyun Gedam
 * Source: https://github.com/pradyunsg/furo/blob/2024.08.06/src/furo/theme/furo/partials/icons.html#L3-L8
 * License: MIT
 */
export const TableOfContentsIcon: React.FC<TableOfContentsIconProps> = ({
  wrapperClassName,
  svgClassName,
}) => {
  return (
    <span className={wrapperClassName}>
      <svg
        aria-hidden="true"
        className={svgClassName || "h-5 w-5"}
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="0"
        viewBox="0 0 1024 1024"
      >
        <title>Contents</title>
        <path d="M408 442h480c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8H408c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8zm-8 204c0 4.4 3.6 8 8 8h480c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8H408c-4.4 0-8 3.6-8 8v56zm504-486H120c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zm0 632H120c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h784c4.4 0 8-3.6 8-8v-56c0-4.4-3.6-8-8-8zM115.4 518.9L271.7 642c5.8 4.6 14.4.5 14.4-6.9V388.9c0-7.4-8.5-11.5-14.4-6.9L115.4 505.1a8.74 8.74 0 0 0 0 13.8z" />
      </svg>
    </span>
  );
};
